import styled, { css } from 'styled-components';
import * as bp from 'view/styles/breakpoints';
import { newElevationsFromDesignSystems } from 'view/styles/elevation';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as zIndex from 'view/styles/z-index';

import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { PortalName, cardStyle, PortalCardContainer } from '../portal-card';
import type { FeaturedProjectCardProps, UnFeaturedProjectCardProps } from './types';

export { Description, IconContainer, Content } from '../portal-card/styled';

const rootBackgroundColor = (props: FeaturedProjectCardProps | UnFeaturedProjectCardProps) =>
    props.isDropped
        ? token('elevation.surface.raised.pressed', colors.B50)
        : token('elevation.surface.raised', colors.N0);

export const Root = styled.div<FeaturedProjectCardProps | UnFeaturedProjectCardProps>`
    ${cardStyle}
    ${(props) =>
        props.isFeatured
            ? mixins.raiseUpOnHoverOrFocus(newElevationsFromDesignSystems.e100, newElevationsFromDesignSystems.e200)
            : newElevationsFromDesignSystems.e100};
    ${(props) => mixins.applyIf(!Boolean(props.isFeatured), 'z-index: 0;')}
    background-color: ${rootBackgroundColor};
    user-select: none;

    ${(props) => css`
        ${props.isFeatured &&
        !props.isDropped &&
        `&:hover {
            background: ${token('color.background.neutral.subtle.hovered', colors.N20)};
        }`};
    `};

    &:hover,
    &:focus {
        z-index: ${zIndex.portalCardLayout};
        text-decoration: none;
    }

    &::after {
        cursor: ${(props) => {
            return props.isFeatured ? 'grab' : 'auto';
        }};
    }
`;

export const Actions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: absolute;
    top: ${token('space.100', '8px')};
    right: ${token('space.050', '4px')};
`;

export const Container = styled(PortalCardContainer)<{ isDraggable?: boolean }>`
    padding-right: ${grid.multiple(5.5).px};
    ${({ isDraggable }) => (isDraggable ? `padding-left: ${token('space.500', '40px')}` : '')};
`;

export const ProjectName = styled(PortalName)`
    color: ${token('color.text', colors.N800)};
`;

export const cardGutterStyle = css`
    margin: ${token('space.200', '16px')} ${token('space.250', '20px')};

    ${bp.fromLarge.css`
margin: ${token('space.200', '16px')} ${grid.multiple(4.75).px};
`}
`;

export const Overlay = styled.div<{ show: boolean }>`
    ${cardStyle}
    ${cardGutterStyle}
    /* stylelint-disable scale-unlimited/declaration-strict-value */
    background-color: ${(props) =>
        props.show
            ? token('color.background.selected.hovered', colors.B75)
            : token('elevation.surface.raised', 'transparent')};
    opacity: 0.3;
    transition: all 0.2s;
`;

export const Wrapper = styled.li`
    ${cardGutterStyle}
    touch-action: none;
    list-style: none;

    &:focus {
        ${mixins.focusRing.default};

        & ${Root} {
            &:hover {
                transform: translate3d(0, 0, 0);
            }
        }
    }

    &.sortable-helper {
        cursor: grabbing !important;
        pointer-events: auto !important;
        list-style: none;

        ${Root} {
            background-color: ${rootBackgroundColor};

            &::after {
                cursor: grabbing;
            }

            &:hover {
                cursor: grabbing;
                background-color: ${token('elevation.surface.raised', colors.N0)};
            }
            box-shadow: ${token(
                'elevation.shadow.overlay',
                `0 ${token('space.100', '8px')} ${token('space.150', '12px')} rgba(0, 0, 0, 0.15)`
            )};
        }
    }
`;

export const DragHandlerWrapper = styled.div`
    left: ${token('space.050', '4px')};
    position: absolute;
`;

export const SortableCardDescription = styled.div`
    display: none;
`;
